import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { Button } from "../button"
import type { ButtonTelemetryProps } from "../button-base"
import { Icon } from "../icon"
import { cn } from "../utils"
import { VisuallyHidden } from "../visually-hidden"
import { useNoteContext } from "./note-context"
import type { FocusTarget } from "./note-focus"

export type NoteDismissProps = {
  className?: string
  /** Called when the note is dismissed, before it unmounts. Use it to persist the dismissal or track it. */
  onDismiss?: () => void
  /** Element to focus after dismissal, as a ref or a function that returns the element. Defaults to the next focusable element after the note, or the previous one when nothing follows. */
  focusTarget?: FocusTarget
  /** Replaces the default close button, e.g. with a `Button` placed inside `Note.Actions`. */
  render?: React.ReactElement
  /** Telemetry for the default close button's `button_click` event. With `render`, set `telemetry` on the rendered button instead. */
  telemetry?: ButtonTelemetryProps["telemetry"]
}

/**
 * A button that dismisses the note. Renders a close button in the note's
 * dismiss slot by default, or the `render` element anywhere in the note.
 */
export function NoteDismiss({
  className,
  onDismiss,
  focusTarget,
  render,
  telemetry
}: NoteDismissProps) {
  const { dismiss } = useNoteContext()

  const trigger = useRender({
    render: render ?? (
      <Button
        className="relative after:content-[''] after:absolute after:-inset-1"
        variant="tertiary"
        size="small"
        telemetry={telemetry}
      >
        <Icon name="close" />
        <VisuallyHidden>Dismiss this message</VisuallyHidden>
      </Button>
    ),
    props: {
      onClick: () => {
        onDismiss?.()
        dismiss(focusTarget)
      }
    }
  })

  if (render) return trigger

  return (
    <div
      className={cn(
        "[grid-area:dismiss] size-6 flex items-center justify-center",
        className
      )}
      data-slot="note-dismiss"
    >
      {trigger}
    </div>
  )
}
